import { create } from 'zustand';
import type { User, UserStats } from '@/types/user';

interface UserStoreState {
  user: User | null;
  stats: UserStats | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setStats: (stats: UserStats | null) => void;
  updateHints: (delta: number) => void;
  updateXP: (delta: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  reset: () => void;
}

export const useUserStore = create<UserStoreState>((set) => ({
  // Initial state
  user: null,
  stats: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  setUser: (user: User | null) => {
    set({
      user,
      isAuthenticated: user !== null,
    });
  },

  setStats: (stats: UserStats | null) => {
    set({ stats });
  },

  updateHints: (delta: number) => {
    set((state) => {
      if (state.user) {
        return {
          user: {
            ...state.user,
            hints: Math.max(0, state.user.hints + delta),
          },
        };
      }
      return state;
    });
  },

  updateXP: (delta: number) => {
    set((state) => {
      if (state.user) {
        const newXP = state.user.totalXP + delta;
        // Calculate level based on XP: 1000 XP = Level 1, 2000 XP = Level 2, etc.
        const newLevel = Math.floor(newXP / 1000);
        
        return {
          user: {
            ...state.user,
            totalXP: newXP,
            level: newLevel,
          },
        };
      }
      return state;
    });
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  setError: (error: string | null) => {
    set({ error });
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({
      user: null,
      stats: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  },
}));

